/**
 * Items: what there is, and how a new one is made.
 *
 * Two screens in one window. The first is everything that exists, sorted into
 * kinds, with what is known about whichever one is picked. The second is the form
 * that makes one, which asks a different set of questions depending on what is
 * being made: a sword wants a speed and a set of bonuses, a fish wants to know
 * how much it puts back, and a quest item wants neither.
 *
 * Saving goes straight through: the server takes the item, validates it, puts it
 * into the world at once and writes the file it came from. There is no separate
 * save button, because an item that exists only until the next restart is a
 * trap rather than a feature.
 */

import type {
  ItemDefinitionWire,
  ObjectDefinitionWire,
  QuestWire,
  RecipeWire,
} from '@inverse/protocol';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Wanted } from './find';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';
import { ActsEditor } from './Acts';
import { ItemPicker } from './ItemPicker';
import {
  ALL_ITEM_TAGS,
  BONUS_FIELDS,
  blankItem,
  draftFrom,
  usesOf,
  ITEM_TAGS,
  EQUIPMENT_SLOTS,
  wears,
  ITEM_KINDS,
  KIND_LABELS,
  RECIPE_SKILLS,
  REQUIREMENT_SKILLS,
  type ItemDetail,
  type ItemDraft,
  type UseDraft,
  type ListedItem,
} from './items';

export function EditorItems({
  list,
  detail,
  saves,
  items,
  recipes,
  wanted,
  onEdit,
  onCommand,
}: {
  /** Everything there is, as the server last listed it. */
  list: readonly ListedItem[];
  /** Whichever item is open, in full. */
  detail: ItemDetail | null;
  /** How many items have been saved this session, which is how a form knows it is done. */
  saves: number;
  /** What the client already knows, for naming an ingredient in a recipe. */
  items: readonly ItemDefinitionWire[];
  /** Every recipe there is, for saying what an item is used in. */
  recipes: readonly RecipeWire[];
  /** An item somewhere else asked for, such as the search across everything. */
  wanted: Wanted | null;
  /**
   * Opens the window that writes one.
   *
   * A window of its own rather than the list turning into a form: the whole
   * point of writing a better sword is being able to read the worse one while
   * doing it.
   */
  onEdit: (draft: ItemDraft) => void;
  onCommand: AdminCommand;
}): JSX.Element {
  const [chosen, setChosen] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');

  const [seenSaves, setSeenSaves] = useState(saves);
  const asked = useRef(false);

  // The list, once, when the window opens. Asking on every render is a loop.
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    onCommand('listItems');
  }, [onCommand]);

  // A save landing is the end of the form: what is on screen afterwards is the
  // item as the server now holds it, rather than what was typed at it.
  if (seenSaves !== saves) {
    setSeenSaves(saves);
    if (detail) setChosen(detail.item.id);
  }

  const held = detail;

  const groups = useMemo(() => {
    const wanted = search.trim().toLowerCase();
    const matching = wanted
      ? list.filter(
          (entry) => entry.name.toLowerCase().includes(wanted) || String(entry.id) === wanted,
        )
      : list;

    return ITEM_KINDS.map((kind) => ({
      kind,
      entries: matching.filter((entry) => entry.kind === kind),
    })).filter((group) => group.entries.length > 0);
  }, [list, search]);

  const open = (id: number): void => {
    setChosen(id);
    onCommand('itemDetails', { item: id });
  };

  // Asked for from outside. Taken once, so pressing another row afterwards is
  // not undone by the request that opened the panel.
  const [took, setTook] = useState(0);
  if (wanted !== null && wanted.at !== took) {
    setTook(wanted.at);
    open(Number(wanted.id));
  }

  const edit = (): void => {
    if (!held) return;
    onEdit({ ...draftFrom(held.item, held.recipe), uses: usesOf(held.item.id, recipes) });
  };

  /**
   * The same thing again, as something new.
   *
   * A tier of armour is the tier below it with bigger numbers, and writing the
   * second one out by hand is how the two end up disagreeing about everything
   * that was meant to stay the same. Opened as a new item with a new name, so
   * saving it cannot overwrite what it was copied from.
   */
  const copy = (): void => {
    if (!held) return;

    const from = { ...draftFrom(held.item, held.recipe), uses: usesOf(held.item.id, recipes) };
    onEdit({ ...from, id: 0, name: `${from.name} Copy` });
  };

  if (picking) {
    return (
      <>
        <h3>Type</h3>
        {ITEM_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className="wide editor-kind"
            onClick={() => {
              setPicking(false);
              onEdit(blankItem(kind));
            }}
          >
            <strong>{KIND_LABELS[kind].one}</strong>
            <span>{KIND_LABELS[kind].about}</span>
          </button>
        ))}

        <button type="button" className="wide" onClick={() => setPicking(false)}>
          Never mind
        </button>
      </>
    );
  }

  return (
    <>
      <button type="button" className="wide primary" onClick={() => setPicking(true)}>
        New item
      </button>

      <label className="editor-field">
        <span>Search</span>
        <input
          type="text"
          value={search}
          placeholder="Copper, 1004, bread"
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      {list.length === 0 ? <p className="hint">Asking the server what there is.</p> : null}

      <div className="editor-list">
        {groups.map((group) => (
          <div className="editor-group" key={group.kind}>
            <h4>
              {KIND_LABELS[group.kind].many}
              <span>{group.entries.length}</span>
            </h4>
            {group.entries.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={entry.id === chosen ? 'editor-row on' : 'editor-row'}
                onClick={() => open(entry.id)}
              >
                <span>{entry.name}</span>
                <span className="editor-row-id">{entry.id}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {held && held.item.id === chosen ? (
        <ItemFacts held={held} items={items} onEdit={edit} onCopy={copy} />
      ) : null}
    </>
  );
}

/** Everything worth reading about an item, without a form in the way. */
function ItemFacts({
  held,
  items,
  onEdit,
  onCopy,
}: {
  held: ItemDetail;
  items: readonly ItemDefinitionWire[];
  /** Absent when this is being read beside something else rather than worked on. */
  onEdit?: () => void;
  onCopy?: () => void;
}): JSX.Element {
  const item = held.item;
  const equipment = item.equipment;
  const nameOf = (id: number): string => items.find((entry) => entry.id === id)?.name ?? `#${id}`;

  const worn = equipment
    ? BONUS_FIELDS.filter(([field]) => (equipment.bonuses?.[field] ?? 0) !== 0)
    : [];

  return (
    <div className="editor-facts">
      <h3>{item.name}</h3>
      <p className="hint">{item.examine || 'Nothing to say about it.'}</p>

      <dl className="editor-pairs">
        <dt>Id</dt>
        <dd>{item.id}</dd>
        <dt>Value</dt>
        <dd>{item.value ?? 0}</dd>
        <dt>A shop</dt>
        <dd>
          sells at {held.buys}, pays {held.sells}
        </dd>
        <dt>Weight</dt>
        <dd>{item.weight ?? 0}</dd>
        <dt>Stackable</dt>
        <dd>{item.stackable ? 'yes' : 'no'}</dd>
        <dt>Tradeable</dt>
        <dd>{item.tradeable === false ? 'never' : 'yes'}</dd>
        {(item.heals ?? 0) > 0 ? (
          <>
            <dt>Heals</dt>
            <dd>{item.heals}</dd>
          </>
        ) : null}
        {(item.actions ?? []).length > 0 ? (
          <>
            <dt>Menu</dt>
            <dd>{(item.actions ?? []).join(', ')}</dd>
          </>
        ) : null}
        {equipment ? (
          <>
            <dt>Worn</dt>
            <dd>
              {equipment.slot}
              {equipment.twoHanded ? ', two handed' : ''}
            </dd>
          </>
        ) : null}
        {equipment?.speed && equipment.slot === 'weapon' ? (
          <>
            <dt>Swings</dt>
            <dd>every {equipment.speed} ticks</dd>
          </>
        ) : null}
        {equipment?.fires ? (
          <>
            <dt>Fires</dt>
            <dd>
              {equipment.fires.kind}, up to tier {equipment.fires.upTo}
            </dd>
          </>
        ) : null}
        {equipment?.ammunition ? (
          <>
            <dt>Fired by</dt>
            <dd>
              anything that fires {equipment.ammunition.kind}, tier {equipment.ammunition.tier}
            </dd>
          </>
        ) : null}
      </dl>

      {worn.length > 0 ? (
        <div className="editor-bonuses">
          {worn.map(([field, label]) => (
            <span key={field}>
              {label} {(equipment?.bonuses?.[field] ?? 0) > 0 ? '+' : ''}
              {equipment?.bonuses?.[field] ?? 0}
            </span>
          ))}
        </div>
      ) : null}

      {Object.entries(equipment?.requirements ?? {}).length > 0 ? (
        <p className="hint">
          Needs{' '}
          {Object.entries(equipment?.requirements ?? {})
            .map(([skill, level]) => `${skill} ${level}`)
            .join(', ')}
          .
        </p>
      ) : null}

      {held.recipe ? (
        <p className="hint">
          {held.recipe.action} {held.recipe.inputQuantity} x {nameOf(held.recipe.inputId)}
          {held.recipe.objectId > 0 ? ' at the right place' : ' in the bag'}, at {held.recipe.skill}{' '}
          {held.recipe.level}. Worth {held.recipe.experience} experience.
        </p>
      ) : (
        <p className="hint">Nothing makes it.</p>
      )}

      {onEdit ? (
        <button type="button" className="wide primary" onClick={onEdit}>
          Edit
        </button>
      ) : null}

      {onCopy ? (
        <button
          type="button"
          className="wide"
          title="Open a new item with everything this one has"
          onClick={onCopy}
        >
          Duplicate
        </button>
      ) : null}
    </div>
  );
}

/**
 * One part of the form, folded away until it is wanted.
 *
 * An item is a dozen questions and any one person is answering two of them.
 * Open by default where the answer is usually needed, shut where it is not.
 */
function Fold({
  title,
  note,
  open,
  onOpen,
  children,
}: {
  title: string;
  note?: string;
  open: boolean;
  onOpen: (open: boolean) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={open ? 'editor-fold open' : 'editor-fold'}>
      <button type="button" className="editor-fold-head" onClick={() => onOpen(!open)}>
        <span className="editor-fold-mark" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
        <span className="editor-fold-name">{title}</span>
        {note ? <small>{note}</small> : null}
      </button>

      {open ? <div className="editor-fold-body">{children}</div> : null}
    </div>
  );
}

/** The form itself, asking whatever the kind of thing being made calls for. */
export function ItemForm({
  draft,
  items,
  objects,
  quests,
  characters,
  shops,
  recipes,
  saying,
  onChange,
  onSave,
  onCancel,
}: {
  draft: ItemDraft;
  items: readonly ItemDefinitionWire[];
  objects: readonly ObjectDefinitionWire[];
  quests: readonly QuestWire[];
  /** For an act that sends somebody to a character or opens a shop. */
  characters: readonly { id: number; name: string }[];
  shops: readonly { id: number; name: string }[];
  /** Every recipe there is, for working out what this one can be told to do. */
  recipes: readonly RecipeWire[];
  saying: string;
  onChange: (draft: ItemDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element {
  const set = (changed: Partial<ItemDraft>): void => onChange({ ...draft, ...changed });

  /**
   * What this item can be told to do, and why.
   *
   * A short list the world already knows rather than anything somebody types:
   * worn if it is worn, eaten if it heals, and whatever recipe takes it out of
   * the bag. A menu entry outside that list is an entry that does nothing when
   * it is pressed, which is worse than not offering it.
   */
  const offered = useMemo((): { action: string; about: string }[] => {
    const found: { action: string; about: string }[] = [];

    if (wears(draft)) {
      const word = draft.kind === 'armour' ? 'Wear' : 'Wield';
      found.push({ action: word, about: `Puts it on, since it is ${draft.kind}.` });
    }

    if (Number(draft.heals) > 0) {
      found.push({ action: 'Eat', about: 'It heals, so it can be eaten.' });
    }

    // Anything done to it out of the bag: burning a log, and whatever else is
    // written as a recipe with nothing to stand at.
    for (const recipe of recipes) {
      if (recipe.objectId !== 0 || recipe.inputId !== draft.id) continue;
      if (found.some((one) => one.action.toLowerCase() === recipe.action.toLowerCase())) continue;

      found.push({ action: recipe.action, about: `Written as something it is used in.` });
    }

    // And whatever it already carries, so nothing somebody set by hand is lost
    // just because the world cannot work out why it is there.
    for (const held of draft.actions.split(',').map((one) => one.trim())) {
      if (held.length === 0) continue;
      if (found.some((one) => one.action.toLowerCase() === held.toLowerCase())) continue;

      found.push({ action: held, about: 'Already set, and nothing here explains it.' });
    }

    return found;
  }, [draft, recipes]);
  const setRecipe = (changed: Partial<ItemDraft['recipe']>): void =>
    onChange({ ...draft, recipe: { ...draft.recipe, ...changed } });

  /**
   * Which parts of the form are open.
   *
   * What it is called and what it is worth are always wanted; bonuses are
   * wanted while a sword is being written and never otherwise. The ones that
   * hold something start open, so nothing already answered is hidden.
   */
  const [shown, setShown] = useState<Record<string, boolean>>(() => ({
    basics: true,
    acts: draft.acts.length > 0,
    worn: wears(draft),
    tags: false,
    gathering: Number(draft.gatherLevel) > 0,
    recipe: draft.recipe.made,
  }));

  const folds = (name: string): { open: boolean; onOpen: (open: boolean) => void } => ({
    open: shown[name] === true,
    onOpen: (open) => setShown((held) => ({ ...held, [name]: open })),
  });

  const text = (
    label: string,
    value: string,
    onText: (value: string) => void,
    placeholder = '',
  ): JSX.Element => (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onText(event.target.value)}
      />
    </label>
  );

  const toggle = (label: string, on: boolean, onToggle: () => void): JSX.Element => (
    <button type="button" className={on ? 'editor-tab on' : 'editor-tab'} onClick={onToggle}>
      {label}
    </button>
  );

  return (
    <>
      <h3>
        {draft.id > 0
          ? `Editing ${draft.name || 'an item'}`
          : `A new ${KIND_LABELS[draft.kind].one.toLowerCase()}`}
      </h3>

      <div className="editor-tabs">
        {ITEM_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={draft.kind === kind ? 'editor-tab on' : 'editor-tab'}
            onClick={() => set({ kind })}
          >
            {KIND_LABELS[kind].one}
          </button>
        ))}
      </div>

      {/* The heading and the kinds stay put, the form scrolls under them, and
          saving stays at the foot. A form of this length used to be read by
          dragging the window taller, which is not reading it. */}
      <div className="editor-body">
        <Fold title="Basics" {...folds('basics')}>
          {text('Name', draft.name, (value) => set({ name: value }), 'Copper ring')}
          {text(
            'Description',
            draft.examine,
            (value) => set({ examine: value }),
            'Plain, and a little tarnished.',
          )}
          {text('Value', draft.value, (value) => set({ value: value.replace(/[^0-9]/g, '') }))}
          {text('Weight', draft.weight, (value) => set({ weight: value.replace(/[^0-9.]/g, '') }))}
          {/* Picked rather than typed. What an item can be told to do is a
              short list the world already knows: worn if it is worn, eaten if
              it heals, and whatever recipe takes it out of the bag. Typing
              anything else produced a menu entry that did nothing. */}
          <label className="editor-field">
            <span>Menu</span>
            <div className="editor-brushes">
              {offered.length === 0 ? (
                <span className="hint">Nothing it can be told to do yet.</span>
              ) : null}

              {offered.map((one) => {
                const held = draft.actions
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter((entry) => entry.length > 0);
                const on = held.some((entry) => entry.toLowerCase() === one.action.toLowerCase());

                return (
                  <button
                    key={one.action}
                    type="button"
                    className={on ? 'editor-tab on' : 'editor-tab'}
                    title={one.about}
                    onClick={() =>
                      set({
                        actions: (on
                          ? held.filter((entry) => entry.toLowerCase() !== one.action.toLowerCase())
                          : [...held, one.action]
                        ).join(', '),
                      })
                    }
                  >
                    {one.action}
                  </button>
                );
              })}
            </div>
          </label>

          {/* And anything the world has no opinion about: an entry somebody
              writes, which an act underneath makes mean something. */}
          <label className="editor-field">
            <span>Another entry</span>
            <input
              type="text"
              placeholder="Read, Rub, Break"
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                const wanted = event.currentTarget.value.trim().slice(0, 24);
                event.currentTarget.value = '';
                if (!wanted) return;

                const held = draft.actions
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter((entry) => entry.length > 0);
                if (held.some((entry) => entry.toLowerCase() === wanted.toLowerCase())) return;
                set({ actions: [...held, wanted].join(', ') });
              }}
            />
          </label>

          <div className="editor-brushes">
            {toggle('Stackable', draft.stackable, () => set({ stackable: !draft.stackable }))}
            {toggle('Tradeable', draft.tradeable, () => set({ tradeable: !draft.tradeable }))}
          </div>

          {draft.kind === 'consumable' ? (
            <>
              {text('Heals', draft.heals, (value) => set({ heals: value.replace(/[^0-9]/g, '') }))}
              <p className="hint">
                Health restored when eaten.
                <Explain>
                  In the same units health is counted in. Eating it uses it up, so give it a menu
                  entry that says so.
                </Explain>
              </p>
            </>
          ) : null}

          {draft.kind === 'quest' ? (
            <>
              {text('Only this many', draft.questAtMost, (value) =>
                set({ questAtMost: value.replace(/[^0-9]/g, '') }),
              )}
              <p className="hint">
                How many of it one person may hold at once.
                <Explain>
                  Counted across the bag, the bank, what they are wearing and the belt, so a second
                  one cannot be fetched by banking the first. Anybody who tries is told a magical
                  force prevents them.
                </Explain>
              </p>

              {text('Another is at', draft.questFoundAt, (value) => set({ questFoundAt: value }))}
              <p className="hint">
                Said to somebody who has just destroyed one.
                <Explain>
                  Dropping a quest item destroys it rather than leaving it on the floor, where
                  somebody else could pick it up. This is the sentence that tells them where to get
                  another, and without it a quest can be made unfinishable by one press.
                </Explain>
              </p>

              <p className="hint">Quest items stay out of trades and shops whatever else they say.</p>
            </>
          ) : null}
        </Fold>

        {/* What each menu entry does. The same builder scenery and characters
            use, because an act is an act wherever it hangs. */}
        <Fold
          title="What Its Menu Does"
          note={draft.acts.length === 0 ? 'nothing written' : `${draft.acts.length}`}
          {...folds('acts')}
        >
          <p className="hint">
            Anything the world does not do on its own: a scroll read, a tablet broken, a ring
            rubbed.
            <Explain>
              Use Take Item on the item itself for anything that is used up by being used.
            </Explain>
          </p>
          <ActsEditor
            acts={draft.acts}
            menu={draft.actions
              .split(',')
              .map((one) => one.trim())
              .filter((one) => one.length > 0)}
            items={items}
            quests={quests}
            characters={characters}
            objects={objects}
            shops={shops}
            onChange={(acts) => set({ acts })}
          />
        </Fold>

        {/* What it is for. Not a rule the world enforces: a hint the tools read,
          so a new hatchet turns up where a tree asks what it needs, and a new
          fish turns up in the list of what a spot holds. */}
        <Fold title="Tags" note={`${draft.tags.length} set`} {...folds('tags')}>
          {Object.entries(ITEM_TAGS).map(([group, tags]) => (
            <div className="editor-brushes" key={group}>
              <span className="editor-small-label">{group}</span>
              {[...tags].map((tag) =>
                toggle(tag.replace(/_/g, ' '), draft.tags.includes(tag), () =>
                  set({
                    tags: draft.tags.includes(tag)
                      ? draft.tags.filter((held) => held !== tag)
                      : [...draft.tags, tag],
                  }),
                ),
              )}
            </div>
          ))}

          {text(
            'And any others',
            draft.tags.filter((tag) => !ALL_ITEM_TAGS.includes(tag)).join(', '),
            (value) =>
              set({
                tags: [
                  ...draft.tags.filter((tag) => ALL_ITEM_TAGS.includes(tag)),
                  ...value
                    .split(',')
                    .map((tag) => tag.trim().toLowerCase().replace(/\s+/g, '_'))
                    .filter((tag) => tag.length > 0),
                ],
              }),
            'lantern, tool_farming',
          )}
        </Fold>

        {/* What it takes to gather this, and what it gives. Here rather than on
          every spot it turns up at: a trout is worth the same wherever it is
          caught, and a level written down four times says four things. */}
        <Fold
          title="Gathering"
          note={Number(draft.gatherLevel) > 0 ? `level ${draft.gatherLevel}` : 'not gathered'}
          {...folds('gathering')}
        >
          {text('Level', draft.gatherLevel, (value) =>
            set({ gatherLevel: value.replace(/[^0-9]/g, '') }),
          )}
          {text('XP', draft.gatherExperience, (value) =>
            set({ gatherExperience: value.replace(/[^0-9.]/g, '') }),
          )}

          {/* How much one spot holds. On the material rather than on the spot,
              because a pine gives an armful and an ironwood gives one log and a
              sore back. Two numbers so a spot is not the same spot twice. */}
          {Number(draft.gatherLevel) > 0 ? (
            <>
              <label className="editor-field">
                <span>Per spot</span>
                <div className="editor-brushes">
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    title="At least this many come out of one spot"
                    value={draft.chargesLeast}
                    onChange={(event) => set({ chargesLeast: event.target.value })}
                  />
                  <span className="quiet">to</span>
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    title="At most this many"
                    value={draft.chargesMost}
                    onChange={(event) => set({ chargesMost: event.target.value })}
                  />
                </div>
              </label>
              <p className="hint">
                {Number(draft.chargesLeast) > 0 || Number(draft.chargesMost) > 0
                  ? `A spot gives ${draft.chargesLeast || draft.chargesMost} to ${draft.chargesMost || draft.chargesLeast} before it is spent.`
                  : 'Nought for both means one and the spot is spent.'}
                <Explain>
                  Rolled once for each spot the first time it is worked, so two trees side by side
                  are not the same tree twice.
                </Explain>
              </p>
            </>
          ) : null}

          <p className="hint">
            Nought means nobody gathers it.
            <Explain>
              A spot says what can be found there and how often. What each of them takes and gives
              is the thing itself, so it is set here and read everywhere.
            </Explain>
          </p>
        </Fold>

        {draft.kind === 'ammunition' ? (
          <div className="editor-brushes">
            {toggle('Worn in the ammunition slot', draft.equips, () =>
              set({ equips: !draft.equips }),
            )}
          </div>
        ) : null}

        {wears(draft) ? (
          <Fold
            title="Equipment"
            note={draft.kind === 'armour' ? draft.slot : draft.kind}
            {...folds('worn')}
          >
            {draft.kind === 'ammunition' ? (
              <>
                {text('Fired by', draft.ammoKind, (value) => set({ ammoKind: value }), 'arrow')}
                {text('Tier', draft.ammoTier, (value) =>
                  set({ ammoTier: value.replace(/[^0-9]/g, '') }),
                )}
                <p className="hint">
                  Any weapon that fires {draft.ammoKind.trim() || 'this kind'} can use it, as long
                  as that weapon takes tier {draft.ammoTier || '1'} or above.
                </p>
              </>
            ) : null}

            {draft.kind === 'armour' ? (
              <label className="editor-field">
                <span>Equip Slot</span>
                <select
                  value={draft.slot}
                  onChange={(event) => set({ slot: event.target.value as ItemDraft['slot'] })}
                >
                  {EQUIPMENT_SLOTS.filter((slot) => slot !== 'weapon').map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {draft.kind === 'weapon' ? (
              <>
                {text('Swings every', draft.speed, (value) =>
                  set({ speed: value.replace(/[^0-9]/g, '') }),
                )}
                <div className="editor-brushes">
                  {toggle('Two handed', draft.twoHanded, () =>
                    set({ twoHanded: !draft.twoHanded }),
                  )}
                </div>
                <p className="hint">
                  In ticks. Four is an ordinary blade, five is heavy, three is quick and weak.
                </p>
                {text('Fires', draft.firesKind, (value) => set({ firesKind: value }), 'nothing')}
                {draft.firesKind.trim() ? (
                  <>
                    {text('Best it takes', draft.firesUpTo, (value) =>
                      set({ firesUpTo: value.replace(/[^0-9]/g, '') }),
                    )}
                    <p className="hint">
                      Ammunition fired by {draft.firesKind.trim()}, up to this tier. A bow fires
                      arrow; leave it empty for a weapon that is swung.
                    </p>
                  </>
                ) : null}
              </>
            ) : null}

            <h4>Requirements</h4>
            <div className="editor-grid">
              {REQUIREMENT_SKILLS.map((skill) => (
                <label className="editor-small" key={skill}>
                  <span>{skill}</span>
                  <input
                    type="text"
                    value={draft.requirements[skill] ?? ''}
                    placeholder="any"
                    onChange={(event) =>
                      set({
                        requirements: {
                          ...draft.requirements,
                          [skill]: event.target.value.replace(/[^0-9]/g, ''),
                        },
                      })
                    }
                  />
                </label>
              ))}
            </div>

            <h4>Attack</h4>
            <div className="editor-grid">
              {BONUS_FIELDS.slice(0, 5).map(([field, label]) => (
                <Bonus key={field} field={field} label={label} draft={draft} onChange={onChange} />
              ))}
            </div>

            <h4>Defence</h4>
            <div className="editor-grid">
              {BONUS_FIELDS.slice(5, 10).map(([field, label]) => (
                <Bonus key={field} field={field} label={label} draft={draft} onChange={onChange} />
              ))}
            </div>

            <h4>Other</h4>
            <div className="editor-grid">
              {BONUS_FIELDS.slice(10).map(([field, label]) => (
                <Bonus key={field} field={field} label={label} draft={draft} onChange={onChange} />
              ))}
            </div>
          </Fold>
        ) : null}

        {/* What it is used in, which is the other half of how it is made and
            used to be reachable only from whatever it turned into. A log is
            burned here rather than on a fire nobody thinks to open. */}
        <Fold
          title="Used In"
          note={draft.uses.length === 0 ? 'nothing' : `${draft.uses.length}`}
          {...folds('uses')}
        >
          {draft.uses.map((use, at) => {
            const setUse = (changed: Partial<UseDraft>): void =>
              set({
                uses: draft.uses.map((one, index) => (index === at ? { ...one, ...changed } : one)),
              });

            return (
              <div className="editor-act" key={at}>
                <div className="editor-brushes">
                  <input
                    type="text"
                    value={use.action}
                    placeholder="Light"
                    title="The menu entry that does it"
                    onChange={(event) => setUse({ action: event.target.value })}
                  />
                  <select
                    value={use.skill}
                    onChange={(event) => setUse({ skill: event.target.value as UseDraft['skill'] })}
                  >
                    {RECIPE_SKILLS.map((skill) => (
                      <option key={skill} value={skill}>
                        {skill}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="editor-tab danger"
                    onClick={() => set({ uses: draft.uses.filter((_, index) => index !== at) })}
                  >
                    ✕
                  </button>
                </div>

                <div className="editor-grid">
                  <label className="editor-small">
                    <span>Level</span>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={use.level}
                      onChange={(event) => setUse({ level: event.target.value })}
                    />
                  </label>
                  <label className="editor-small">
                    <span>XP</span>
                    <input
                      type="number"
                      min={0}
                      value={use.experience}
                      onChange={(event) => setUse({ experience: event.target.value })}
                    />
                  </label>
                </div>

                <label className="editor-field">
                  <span>Where</span>
                  <select
                    value={use.objectId}
                    onChange={(event) => setUse({ objectId: event.target.value })}
                  >
                    <option value="0">out of the bag</option>
                    {objects.map((one) => (
                      <option key={one.id} value={String(one.id)}>
                        {one.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="editor-field">
                  <span>Turns into</span>
                  <select
                    value={use.outputId}
                    onChange={(event) => setUse({ outputId: event.target.value })}
                  >
                    <option value="0">nothing, it is used up</option>
                    {items.map((one) => (
                      <option key={one.id} value={String(one.id)}>
                        {one.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            );
          })}

          <button
            type="button"
            className="wide"
            onClick={() =>
              set({
                uses: [
                  ...draft.uses,
                  {
                    skill: 'firemaking',
                    action: 'Light',
                    level: '1',
                    experience: '40',
                    objectId: '0',
                    outputId: '0',
                    outputQuantity: '1',
                  },
                ],
              })
            }
          >
            Add A Use
          </button>

          <p className="hint">
            What is done to it rather than what makes it.
            <Explain>
              A log is burned, an ore is smelted, a fish is cooked. Anything here with nowhere to
              stand happens straight out of the bag, and shows up as a menu entry on the item.
            </Explain>
          </p>
        </Fold>

        <Fold title="Recipe" note={draft.recipe.made ? 'made' : 'not made'} {...folds('recipe')}>
          <div className="editor-brushes">
            {toggle('Something makes it', draft.recipe.made, () =>
              setRecipe({ made: !draft.recipe.made }),
            )}
          </div>

          {draft.recipe.made ? (
            <>
              <label className="editor-field">
                <span>Skill</span>
                <select
                  value={draft.recipe.skill}
                  onChange={(event) =>
                    setRecipe({ skill: event.target.value as ItemDraft['recipe']['skill'] })
                  }
                >
                  {RECIPE_SKILLS.map((skill) => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>
              </label>

              {text('At level', draft.recipe.level, (value) =>
                setRecipe({ level: value.replace(/[^0-9]/g, '') }),
              )}
              {text('Worth', draft.recipe.experience, (value) =>
                setRecipe({ experience: value.replace(/[^0-9.]/g, '') }),
              )}

              <label className="editor-field">
                <span>Ingredients</span>
                <ItemPicker
                  items={[...items].sort((a, b) => a.name.localeCompare(b.name))}
                  value={Number(draft.recipe.inputId)}
                  tag="material"
                  allowNothing
                  nothingLabel="Choose something"
                  onPick={(itemId) => setRecipe({ inputId: String(itemId) })}
                />
              </label>

              {/* Everything else it takes. A net full of deep water fish wants the
              arms as much as the eye, and one skill could not say so. */}
              <div className="editor-section">
                <h4>Also Needs</h4>
                {draft.recipe.alsoNeeds.map((need, which) => (
                  <div className="editor-condition" key={which}>
                    <select
                      value={need.skill}
                      onChange={(event) =>
                        setRecipe({
                          alsoNeeds: draft.recipe.alsoNeeds.map((one, index) =>
                            index === which
                              ? { ...one, skill: event.target.value as typeof one.skill }
                              : one,
                          ),
                        })
                      }
                    >
                      {RECIPE_SKILLS.map((skill) => (
                        <option key={skill} value={skill}>
                          {skill}
                        </option>
                      ))}
                    </select>

                    <label className="editor-number">
                      <span>level</span>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={need.level}
                        onChange={(event) =>
                          setRecipe({
                            alsoNeeds: draft.recipe.alsoNeeds.map((one, index) =>
                              index === which ? { ...one, level: event.target.value } : one,
                            ),
                          })
                        }
                      />
                    </label>

                    <button
                      type="button"
                      className="editor-tab"
                      aria-label="Remove"
                      onClick={() =>
                        setRecipe({
                          alsoNeeds: draft.recipe.alsoNeeds.filter((_, index) => index !== which),
                        })
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="editor-tab quiet"
                  onClick={() =>
                    setRecipe({
                      alsoNeeds: [...draft.recipe.alsoNeeds, { skill: 'smithing', level: '10' }],
                    })
                  }
                >
                  Add Skill
                </button>
              </div>

              {/* And the gate in front of it: a recipe nobody knows about until a
              quest teaches it, or until something writes a flag down. */}
              <div className="editor-section">
                <h4>Unlocked By</h4>
                <label className="editor-field">
                  <span>Quest</span>
                  <select
                    value={draft.recipe.lockQuest}
                    onChange={(event) => setRecipe({ lockQuest: event.target.value })}
                  >
                    <option value="">Known From The Start</option>
                    {[...quests]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((quest) => (
                        <option key={quest.id} value={quest.id}>
                          {quest.name}
                        </option>
                      ))}
                  </select>
                </label>

                {draft.recipe.lockQuest ? (
                  <label className="editor-field">
                    <span>From Step</span>
                    <select
                      value={draft.recipe.lockAtLeast}
                      onChange={(event) => setRecipe({ lockAtLeast: event.target.value })}
                    >
                      <option value="1">Started</option>
                      {(
                        quests.find((quest) => quest.id === draft.recipe.lockQuest)?.steps ?? []
                      ).map((step, index) => (
                        <option key={index} value={String(index + 1)}>
                          Step {index + 1}: {step.text.slice(0, 32)}
                        </option>
                      ))}
                      <option
                        value={String(
                          (quests.find((quest) => quest.id === draft.recipe.lockQuest)?.steps
                            .length ?? 0) + 1,
                        )}
                      >
                        Finished
                      </option>
                    </select>
                  </label>
                ) : null}

                {text('Flag', draft.recipe.lockFlag, (value) => setRecipe({ lockFlag: value }))}
                {draft.recipe.lockFlag
                  ? text('Flag Value', draft.recipe.lockFlagIs, (value) =>
                      setRecipe({ lockFlagIs: value }),
                    )
                  : null}
              </div>

              {text('How many of them', draft.recipe.inputQuantity, (value) =>
                setRecipe({ inputQuantity: value.replace(/[^0-9]/g, '') }),
              )}
              {text('How many come out', draft.recipe.outputQuantity, (value) =>
                setRecipe({ outputQuantity: value.replace(/[^0-9]/g, '') }),
              )}

              <label className="editor-field">
                <span>Station</span>
                <select
                  value={draft.recipe.objectId}
                  onChange={(event) => setRecipe({ objectId: event.target.value })}
                >
                  <option value="0">In the bag, wherever they are</option>
                  {[...objects]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                      </option>
                    ))}
                </select>
              </label>

              {text(
                'Called',
                draft.recipe.action,
                (value) => setRecipe({ action: value }),
                'Smelt',
              )}
              {text('Goes wrong', draft.recipe.failureChance, (value) =>
                setRecipe({ failureChance: value.replace(/[^0-9]/g, '') }),
              )}
              <p className="hint">
                Out of a hundred, at the level it asks for. The chance falls away as somebody
                outgrows it.
              </p>

              {/* Anywhere else the same thing is made. A stew is a stew whether it
              is cooked on a fire or on a stove. */}
              <h4>Also Made At</h4>
              {draft.recipe.alsoAt.map((where, which) => (
                <div className="editor-condition" key={which}>
                  <select
                    value={where}
                    onChange={(event) =>
                      setRecipe({
                        alsoAt: draft.recipe.alsoAt.map((one, index) =>
                          index === which ? event.target.value : one,
                        ),
                      })
                    }
                  >
                    <option value="0">Choose a station</option>
                    {[...objects]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.name}
                        </option>
                      ))}
                  </select>

                  <button
                    type="button"
                    className="editor-tab"
                    aria-label="Remove this station"
                    onClick={() =>
                      setRecipe({
                        alsoAt: draft.recipe.alsoAt.filter((_, index) => index !== which),
                      })
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="editor-tab quiet"
                onClick={() => setRecipe({ alsoAt: [...draft.recipe.alsoAt, '0'] })}
              >
                Add Station
              </button>

              <div className="editor-brushes">
                {toggle('Hidden from the skill guide', draft.recipe.hidden, () =>
                  setRecipe({ hidden: !draft.recipe.hidden }),
                )}
              </div>
            </>
          ) : null}
        </Fold>
      </div>

      {saying ? <p className="hint warning">{saying}</p> : null}

      <button type="button" className="wide primary" onClick={onSave}>
        Save Item
      </button>
      <button type="button" className="wide" onClick={onCancel}>
        Back to the list
      </button>
    </>
  );
}

/** One bonus box. There are fourteen of them and they all behave the same way. */
function Bonus({
  field,
  label,
  draft,
  onChange,
}: {
  field: string;
  label: string;
  draft: ItemDraft;
  onChange: (draft: ItemDraft) => void;
}): JSX.Element {
  return (
    <label className="editor-small">
      <span>{label}</span>
      <input
        type="text"
        value={draft.bonuses[field] ?? '0'}
        onChange={(event) =>
          onChange({
            ...draft,
            bonuses: { ...draft.bonuses, [field]: event.target.value.replace(/[^0-9-]/g, '') },
          })
        }
      />
    </label>
  );
}
